import { pathOr } from 'ramda';

import { PAYMENT_METHOD } from 'constants/all';

import {
  getQuoteItemConfiguration,
  getSelectedOptions,
} from 'modules/digiQuote/digiQuotePage/data/reducer';

import { percentage } from 'utils/numberUtils';

import { SECTION_NAME } from './constants';

const getScope = (state) => state.modules.digiQuote.checkoutPage;

export const getInitialFormData = (state) => getScope(state).initialFormData;

export const getIsSubmitting = (state) => getScope(state).isSubmitting;

export const getError = (state) => getScope(state).error;

export const getStripeError = (state) => getScope(state).stripeError;

export const getCheckoutData = (state) => getScope(state).checkoutData;

export const getInitialShippingAddress = (state) => {
  const checkoutData = getCheckoutData(state);
  const initialFormData = getInitialFormData(state);
  return checkoutData.shippingAddress
    ? checkoutData.shippingAddress
    : initialFormData.initialAddress;
};

export const getInitialBillingAddress = (state) => {
  const checkoutData = getCheckoutData(state);
  const initialFormData = getInitialFormData(state);
  return checkoutData.billingAddress
    ? checkoutData.billingAddress
    : initialFormData.initialAddress;
};

export const getConfirmedPaymentMethod = (state) =>
  getCheckoutData(state).paymentMethod;

export const getConfirmedShippingAddress = (state) =>
  getCheckoutData(state).shippingAddress;

const getConfirmedShippingCountry = (state) =>
  pathOr(
    null,
    ['address', 'country', 'abbr'],
    getCheckoutData(state).shippingAddress,
  );

export const getConfirmedBillingAddress = (state) => {
  const checkoutData = getCheckoutData(state);
  return checkoutData.useShippingAddress
    ? checkoutData.shippingAddress || null
    : checkoutData.billingAddress || null;
};

export const getConfirmedShippingOption = (state) =>
  getCheckoutData(state).shippingOption;

export const getConfirmedShippingRate = (state) =>
  getCheckoutData(state).shippingRate;

export const getQuoteItems = (state) => getScope(state).quoteItems;

export const getQuoteToken = (state) => getScope(state).quoteToken;

const getDigiQuoteSubTotalPrice = (state) => {
  const quoteItems = getQuoteItems(state);
  const configuration = getQuoteItemConfiguration(state);
  return quoteItems.reduce((sum, quoteItem) => {
    const quantities = quoteItem.rootComponent.quantities || [];
    const { quantityId, expediteId } = configuration[quoteItem.id] || {};
    const selectedQuantity =
      quantities.find((qty) => quantityId == qty.id) || {};
    const selectedExpedite =
      (selectedQuantity.expedites || []).find(
        (expedite) => expedite.id == expediteId,
      ) || null;
    const additionalPrice = selectedExpedite
      ? selectedExpedite.totalPrice
      : selectedQuantity.totalPrice
      ? selectedQuantity.totalPrice
      : 0;
    return sum + parseFloat(additionalPrice);
  }, 0);
};

export const getSubTotalPrice = (state) => getDigiQuoteSubTotalPrice(state);

export const getSubtotalPriceWithAddOns = (state) => {
  const subtotalPrice = getDigiQuoteSubTotalPrice(state);
  const quoteItems = getQuoteItems(state);
  const selectOptions = getSelectedOptions(state);
  let addOnsPrice = 0;
  quoteItems.forEach((quoteItem) => {
    const options = selectOptions[quoteItem.id];
    options &&
      options.addOnCellIds &&
      options.addOnCellIds.forEach((cellId) => {
        const cell = quoteItem.rootComponent.addOnCells.find(
          (c) => c.id == cellId,
        );
        cell &&
          (addOnsPrice += parseFloat(
            cell.manualPrice ? cell.manualPrice : cell.price,
          ));
      });
  });
  return addOnsPrice + subtotalPrice;
};

const getQuoteData = (state) => getScope(state).quoteData;

export const getSupplier = (state) => (getQuoteData(state) || {}).supplier;

const getTaxRate = (state) => (getQuoteData(state) || {}).taxRate;

export const getTaxCost = (state, selectedShippingRate) => {
  const deprecatedTaxCost = parseFloat((getQuoteData(state) || {}).taxCost);
  if (deprecatedTaxCost) return deprecatedTaxCost;
  const quoteTaxCost = percentage(getSubTotalPrice(state), getTaxRate(state));
  const shippingRate = selectedShippingRate || getConfirmedShippingRate(state);
  const shippingTaxCost = shippingRate
    ? percentage(shippingRate.price, getScope(state).shippingTaxRate)
    : 0;
  return quoteTaxCost + shippingTaxCost;
};

export const getTotalPrice = (state) =>
  getDigiQuoteSubTotalPrice(state) + parseFloat(getTaxCost(state));

export const getCreditCardCheckoutEnabled = (state) =>
  (getQuoteData(state) || {}).creditCardCheckout;

export const getAccount = (state) => (getQuoteData(state) || {}).account;

export const getIsAddressValidating = (state) =>
  getScope(state).addressValidation.isValidating;

export const getIsAddressValid = (state) =>
  getScope(state).addressValidation.isValid;

export const getAddressValidationError = (state) =>
  getScope(state).addressValidation.error;

export const getAddressValidationModalData = (state) => {
  const validationState = getScope(state).addressValidation;
  return {
    isModalOpen: validationState.isModalOpen,
    originalAddress: validationState.originalAddress,
    suggestedAddresses: validationState.suggestedAddresses,
  };
};

export const getIsSectionOpenMapping = (state) =>
  getScope(state).isSectionOpenMapping;

export const getValidationStatus = (state) => {
  const checkoutData = getScope(state).checkoutData;
  const isPaymentMethodValid = !!checkoutData.paymentMethod;
  const isShippingValid = !!checkoutData.shippingAddress;
  const isBillingValid =
    checkoutData.useShippingAddress || !!checkoutData.billingAddress;
  const isSummaryValid = !!checkoutData.shippingOption;
  const isPaymentValid =
    (checkoutData.paymentMethod === PAYMENT_METHOD.CREDIT_CARD &&
      checkoutData.creditCardDetails) ||
    (checkoutData.paymentMethod === PAYMENT_METHOD.PURCHASE_ORDER &&
      checkoutData.purchaseOrderDetails);
  return {
    isValid:
      isPaymentMethodValid &&
      isShippingValid &&
      isBillingValid &&
      isSummaryValid &&
      isPaymentValid,
    [SECTION_NAME.PAYMENT_METHOD]: isPaymentMethodValid,
    [SECTION_NAME.SHIPPING_INFO]: isShippingValid,
    [SECTION_NAME.BILLING_INFO]: isBillingValid,
    [SECTION_NAME.SUMMARY]: isSummaryValid,
    [SECTION_NAME.PAYMENT_DETAILS]: isPaymentValid,
  };
};

export const getSectionAvailability = (state) => {
  const validationStatus = getValidationStatus(state);
  return {
    [SECTION_NAME.PAYMENT_METHOD]: true,
    [SECTION_NAME.SHIPPING_INFO]: validationStatus[SECTION_NAME.PAYMENT_METHOD],
    [SECTION_NAME.BILLING_INFO]: validationStatus[SECTION_NAME.SHIPPING_INFO],
    [SECTION_NAME.SUMMARY]: validationStatus[SECTION_NAME.BILLING_INFO],
    [SECTION_NAME.PAYMENT_DETAILS]: validationStatus[SECTION_NAME.SUMMARY],
  };
};

export const getNextSection = (state, currentSection) => {
  const nextSection =
    currentSection === SECTION_NAME.PAYMENT_METHOD
      ? SECTION_NAME.SHIPPING_INFO
      : currentSection === SECTION_NAME.SHIPPING_INFO
      ? SECTION_NAME.BILLING_INFO
      : currentSection === SECTION_NAME.BILLING_INFO
      ? SECTION_NAME.SUMMARY
      : currentSection === SECTION_NAME.SUMMARY
      ? SECTION_NAME.PAYMENT_DETAILS
      : null;
  return nextSection && getValidationStatus(state)[nextSection]
    ? getNextSection(state, nextSection)
    : nextSection;
};

export const getSections = (state) => {
  const ret = [];
  for (const key in SECTION_NAME) ret.push(key);
  if (!getCreditCardCheckoutEnabled(state))
    ret.splice(ret.indexOf(SECTION_NAME.PAYMENT_METHOD), 1);
  return ret;
};

export const getShippingRatesInfo = (state) => getScope(state).shippingRates;

export const getUrlParams = (state) => getScope(state).urlParams;

export const getCanEstimateShipping = (state) =>
  getScope(state).canEstimateShipping &&
  getConfirmedShippingCountry(state) == 'USA';

export const getShippingDate = (state) => getScope(state).shipDate;

export const getPurchaseOrder = (state) =>
  (getQuoteData(state) || {}).purchaseOrder;

/*
Purchase Orders are allowed for customers with payment terms and no set credit limit,
Or customers with a set credit limit whose order total is less than or equal to that limit
*/
export const getIsPurchaseOrderAllowed = (state) => {
  const purchaseOrder = getPurchaseOrder(state) || {};
  const creditLine = purchaseOrder.creditLine;
  return (
    (creditLine && getTotalPrice(state) <= creditLine) ||
    (purchaseOrder.paymentTerms && !creditLine)
  );
};
